import { __assign } from 'tslib';
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { classNames } from 'uitk-react-utils';
import { debounce } from 'throttle-debounce';
import UitkMonth from './uitk-month.esm.js';
import { isLaterDate, isSameDate, compareDateStrings } from '../generic-utils/dateUtils.esm.js';
import { DefaultDayTemplate } from '../DayTemplateToolkit/DefaultDayTemplate/uitk-default-day-template.esm.js';
import styles from '../uitk-calendar.css';
import AriaLive from './uitk-aria-live.esm.js';
import { useCalendarDays } from './useCalendarDays.esm.js';
import { useDayFocus } from './useDayFocus.esm.js';
import dateUtils from './calendar-utils/dateUtils.esm.js';

/* eslint-disable @typescript-eslint/no-empty-function */
var getClassNameList = classNames.bind(styles);
var dateMatchesMonth = function (date, month, year) {
    return date.getMonth() === month && date.getFullYear() === year;
};
var isSelectionStartInMonth = function (month, year, startDateString) {
    if (!startDateString) {
        return false;
    }
    var startDate = dateUtils.getDateFromString(startDateString);
    return dateMatchesMonth(startDate, month, year);
};
var getRangeSelection = function (day, _a) {
    var selectionStart = _a.selectionStart, selectionEnd = _a.selectionEnd, selectionChangeMode = _a.selectionChangeMode, enableSameDayDateRange = _a.enableSameDayDateRange;
    // If there is already a start of selection, but no end
    if (selectionStart && (!selectionEnd || selectionChangeMode === 'end')) {
        var dateCompare = compareDateStrings(selectionStart, day.dateString);
        var datesAreTheSame = dateCompare === 0;
        // Is the incoming date greater than the selectionStart or
        // is the same day selected and same day selection allowed?
        if (dateCompare > 0 || (datesAreTheSame && enableSameDayDateRange)) {
            return {
                selectionStart: selectionStart,
                selectionEnd: day.dateString,
                focusedDay: day
            };
        }
        if (datesAreTheSame) {
            // If incoming date equals selectionStart cancel out the selection
            return {
                selectionStart: null,
                selectionEnd: null,
                focusedDay: day // Keep day focused for keyboard interaction
            };
        }
        return {
            selectionStart: day.dateString,
            selectionEnd: null,
            focusedDay: day
        };
    }
    return {
        selectionStart: day.dateString,
        selectionEnd: null,
        focusedDay: day
    };
};
var UitkBaseCalendar = function (props) {
    var className = props.className, customAriaLabels = props.customAriaLabels, dataTestId = props.dataTestId, dayData = props.dayData, _a = props.dayNameLength, dayNameLength = _a === void 0 ? 'narrow' : _a, _b = props.dayTemplateComponent, dayTemplateComponent = _b === void 0 ? DefaultDayTemplate : _b, enablePastDates = props.enablePastDates, enableSameDayDateRange = props.enableSameDayDateRange, handleShape = props.handleShape, isSingleDateSelection = props.isSingleDateSelection, 
    /** A moniker or BCP-47 style language tag string. */
    locale = props.locale, maxDate = props.maxDate, minDayCellHeight = props.minDayCellHeight, month = props.month, monthLabelAlignment = props.monthLabelAlignment, _c = props.numColumns, numColumns = _c === void 0 ? 1 : _c, _d = props.numMonthsToDisplay, numMonthsToDisplay = _d === void 0 ? 1 : _d, _e = props.onArrowNavigation, onArrowNavigation = _e === void 0 ? function () { } : _e, onDisabledDayClick = props.onDisabledDayClick, _f = props.onHoverDay, onHoverDay = _f === void 0 ? function () { } : _f, onScrollHandler = props.onScrollHandler, _g = props.onSelectionChanged, onSelectionChanged = _g === void 0 ? function () { } : _g, padWeeks = props.padWeeks, preventScrollOnDayFocus = props.preventScrollOnDayFocus, scrollToMonth = props.scrollToMonth, scrollToMonthRef = props.scrollToMonthRef, selectable = props.selectable, selectionChangeMode = props.selectionChangeMode, selectionEnd = props.selectionEnd, selectionStart = props.selectionStart, firstMonthCallback = props.firstMonthCallback, secondMonthCallback = props.secondMonthCallback, lastMonthCallback = props.lastMonthCallback, showGridlines = props.showGridlines, showMonthLabels = props.showMonthLabels, _h = props.showSiblingMonths, showSiblingMonths = _h === void 0 ? false : _h, skipToButtonRef = props.skipToButtonRef, _j = props.stretchy, stretchy = _j === void 0 ? false : _j, submitButtonLabel = props.submitButtonLabel, year = props.year;
    var l10n = useLocalization();
    var monthsData = useCalendarDays({
        numMonthsToDisplay: numMonthsToDisplay,
        year: year,
        month: month,
        showSiblingMonths: showSiblingMonths,
        locale: locale,
        maxDate: maxDate,
        padWeeks: padWeeks
    });
    var _k = useDayFocus(monthsData, onArrowNavigation), focusedDay = _k.focusedDay, dayFocusActions = _k.dayFocusActions;
    var isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;
    React.useEffect(function () {
        // If dates are cleared, we need to clear the focus state
        if (Object.is(selectionStart, null)) {
            dayFocusActions.clearFocusedDay();
        }
        // Do not add dayFocusActions as a dependency. We only need to check if selectionStart has changed
        // Otherwise focusedDay might be cleared when new monthData is fetched
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionStart]);
    var handleDayClick = React.useCallback(function (date) {
        var dateString = date.dateString;
        if (!dateString || !selectable) {
            return;
        }
        if (isSingleDateSelection) {
            onSelectionChanged(dateString);
        }
        else {
            var _a = getRangeSelection(date, { selectionStart: selectionStart, selectionEnd: selectionEnd, selectionChangeMode: selectionChangeMode, enableSameDayDateRange: enableSameDayDateRange }), newFocusDay = _a.focusedDay, newSelectionStart = _a.selectionStart, newSelectionEnd = _a.selectionEnd;
            if (!isTouch) {
                dayFocusActions.setFocusedDay(newFocusDay);
            }
            // Notify the outside world
            onSelectionChanged(newSelectionStart, newSelectionEnd);
        }
    }, [
        dayFocusActions,
        enableSameDayDateRange,
        isSingleDateSelection,
        isTouch,
        onSelectionChanged,
        selectable,
        selectionChangeMode,
        selectionEnd,
        selectionStart
    ]);
    var handleUnconfirmedSelectionHover = React.useMemo(function () {
        return debounce(35, function (date) {
            var dateString = date.dateString;
            if (selectionStart && !selectionEnd && selectable) {
                var isValidPreselectionHoveredDate = isLaterDate(selectionStart, dateString) ||
                    (enableSameDayDateRange && isSameDate(selectionStart, dateString));
                if (isSingleDateSelection) {
                    onHoverDay(dateString);
                }
                else if (isValidPreselectionHoveredDate) {
                    // We change focus to reset and sync any focus done previously with a keyboard
                    dayFocusActions.setFocusedDay(date);
                    onHoverDay(dateString);
                }
            }
        });
    }, [
        dayFocusActions,
        enableSameDayDateRange,
        isSingleDateSelection,
        onHoverDay,
        selectable,
        selectionEnd,
        selectionStart
    ]);
    var handleDayKeyboardEvents = React.useCallback(function (e, day) {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        dayFocusActions.moveFocusedDayWithArrows(e.key, day);
    }, [dayFocusActions]);
    /**
     *
     * @param buttonRef
     * @returns a skip to submit* button that is invisible to non-google-talkback accessibility tool users. More information found here https://github.expedia.biz/egds/uitk-react/pull/5300#issuecomment-1885084
     */
    var skipToButton = function (buttonRef) {
        if (!buttonRef) {
            return;
        }
        var onClick = function () {
            if (buttonRef && buttonRef.current) {
                buttonRef.current.focus();
            }
        };
        return (React.createElement("button", { className: getClassNameList('is-visually-hidden', 'uitk-calendar-skip-to-button'), onClick: onClick, tabIndex: -1, type: "button" }, submitButtonLabel
            ? l10n.formatText('calendar.skipToButton', submitButtonLabel)
            : l10n.formatText('calendar.default.skipToButton')));
    };
    var calendarClasses = getClassNameList(className, 'uitk-calendar', {
        'uitk-calendar-day-selection-rectangle': handleShape === 'rectangle',
        'uitk-calendar-day-selection-circle': handleShape === 'circle',
        'uitk-no-gridlines': !showGridlines,
        'uitk-calendar-stretchy': stretchy
    });
    var calendarProps = __assign({ className: calendarClasses }, (dataTestId && { 'data-stid': "".concat(dataTestId, "-calendar") }));
    var unconfirmedSelectionEnd = selectionStart && !selectionEnd && focusedDay ? focusedDay.dateString : null;
    var todaysDate = new Date();
    return (React.createElement("div", __assign({}, calendarProps, { role: "application" }),
        React.createElement(AriaLive, __assign({}, { customAriaLabels: customAriaLabels, selectionStart: selectionStart, selectionEnd: selectionEnd, locale: locale })),
        monthsData.map(function (monthData, index) {
            // If this is the current month or the month contains the selected dates, then scroll into view
            var shouldScrollIntoView = !!scrollToMonth &&
                ((!selectionStart && dateMatchesMonth(todaysDate, monthData.month, monthData.year)) ||
                    isSelectionStartInMonth(monthData.month, monthData.year, selectionStart));
            var isFirstMonth = index === 0;
            var isSecondMonth = index === 1;
            var isLastMonth = index === monthsData.length - 1;
            return (React.createElement(React.Fragment, null,
                React.createElement(UitkMonth, __assign({ key: "month-".concat(monthData.year, "-").concat(monthData.month) }, {
                    index: index,
                    year: year,
                    customAriaLabels: customAriaLabels,
                    dayData: dayData,
                    dayNameLength: dayNameLength,
                    dayTemplateComponent: dayTemplateComponent,
                    enablePastDates: enablePastDates,
                    enableSameDayDateRange: enableSameDayDateRange,
                    focusedDay: focusedDay,
                    handleArrowKeyPress: handleDayKeyboardEvents,
                    isFirstMonth: isFirstMonth,
                    isLastMonth: isLastMonth,
                    isSecondMonth: isSecondMonth,
                    isSingleDateSelection: isSingleDateSelection,
                    locale: locale,
                    l10n: l10n,
                    maxDate: maxDate,
                    minDayCellHeight: minDayCellHeight,
                    monthLabelAlignment: monthLabelAlignment,
                    monthData: monthData,
                    numColumns: numColumns,
                    numMonthsToDisplay: numMonthsToDisplay,
                    onDayClick: handleDayClick,
                    onDayHover: handleUnconfirmedSelectionHover,
                    onDisabledDayClick: onDisabledDayClick,
                    onScrollHandler: onScrollHandler,
                    padWeeks: padWeeks,
                    preventScrollOnDayFocus: preventScrollOnDayFocus,
                    scrollToMonth: scrollToMonth,
                    scrollToMonthRef: scrollToMonthRef,
                    selectable: selectable,
                    selectionEnd: selectionEnd,
                    selectionStart: selectionStart,
                    firstMonthCallback: firstMonthCallback,
                    secondMonthCallback: secondMonthCallback,
                    lastMonthCallback: lastMonthCallback,
                    shouldScrollIntoView: shouldScrollIntoView,
                    showMonthLabels: showMonthLabels,
                    showSiblingMonths: showSiblingMonths,
                    stretchy: stretchy,
                    unconfirmedSelectionEnd: unconfirmedSelectionEnd
                })),
                skipToButton && skipToButton(skipToButtonRef)));
        })));
};
UitkBaseCalendar.displayName = 'UitkBaseCalendar';
UitkBaseCalendar.defaultProps = {
    dayData: { defaults: {} },
    dayTemplateComponent: DefaultDayTemplate,
    enablePastDates: false,
    enableSameDayDateRange: false,
    handleShape: 'rectangle',
    monthLabelAlignment: 'center',
    onDisabledDayClick: function () { },
    padWeeks: false,
    selectable: false,
    selectionChangeMode: 'start',
    selectionEnd: null,
    selectionStart: null,
    showGridlines: false,
    showMonthLabels: true
};

export { UitkBaseCalendar };
